"use client"

import * as React from "react"
import { TermCard, NoTermsCard } from "./term-card"
import { DashboardSectionTitle } from "@/components/dashboard-section-heading"
import type { EvaluationTerm } from "@/lib/mock/evaluation-dashboard"

interface TermKanbanProps {
  currentTerm?: EvaluationTerm
  lastTerm?: EvaluationTerm
  showNoTerms?: boolean
}

export function TermKanban({ currentTerm, lastTerm, showNoTerms }: TermKanbanProps) {
  return (
    <section aria-labelledby="eval-kanban-heading" className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <DashboardSectionTitle as="h2" id="eval-kanban-heading">
          Getting feedback
        </DashboardSectionTitle>
        <p className="text-sm text-muted-foreground">
          {currentTerm?.termName ?? "Current term"} in progress
          {lastTerm ? ` · ${lastTerm.termName} ready for review` : ""}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.45fr)_minmax(0,1fr)]">
        <KanbanColumn label="Current term" emphasis>
          {showNoTerms ? (
            <NoTermsCard />
          ) : currentTerm ? (
            <TermCard term={currentTerm} column="current" />
          ) : (
            <EmptyColumn message="No current term configured" />
          )}
        </KanbanColumn>

        <KanbanColumn label="Last term">
          {lastTerm ? (
            <div id={`term-${lastTerm.id}`}>
              <TermCard term={lastTerm} column="last" />
            </div>
          ) : (
            <EmptyColumn message="No recent term to review" />
          )}
        </KanbanColumn>
      </div>
    </section>
  )
}

function KanbanColumn({
  label,
  emphasis,
  children,
}: {
  label: string
  emphasis?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="flex min-h-0 flex-col gap-2">
      <p
        className={
          emphasis
            ? "px-0.5 text-sm font-semibold text-foreground"
            : "px-0.5 text-sm font-medium text-muted-foreground"
        }
      >
        {label}
      </p>
      {children}
    </div>
  )
}

function EmptyColumn({ message }: { message: string }) {
  return (
    <div className="flex min-h-[12rem] items-center justify-center rounded-xl border border-dashed border-border bg-muted/20 px-4 py-8">
      <p className="text-center text-sm text-muted-foreground">{message}</p>
    </div>
  )
}
